// storybook-check-ignore
import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';

import { css } from '@emotion/react';
import Box from '@opendoor/bricks/core/Box';
import { novo } from '@opendoor/bricks/theme';

import { EiChevronDownIcon } from '../../../shared/EiChevronDownIcon';
import { megaNavData } from './megaNavData';
import MegaNavFlyout from './MegaNavFlyout';

const HEADER_HEIGHT = '72px';

interface MegaNavProps {
  slug: string;
}

export const MegaNav: React.FC<MegaNavProps> = ({ slug }) => {
  const [openNavItem, setOpenNavItem] = useState<string | null>(null);
  const navRef = useRef<HTMLDivElement>(null);

  const toggleNavItem = (itemName: string) => {
    setOpenNavItem(openNavItem === itemName ? null : itemName);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setOpenNavItem(null);
      }
    };

    if (openNavItem) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openNavItem]);

  return (
    <Box ref={navRef} gridColumn="1 / -1" gridRow="1 / 2" position="relative">
      <Box
        as="ul"
        m="0"
        p="0"
        display={['none', null, 'flex']}
        justifyContent="center"
        analyticsName="cosmos-mega-nav-menu-list"
        css={css`
          list-style: none;
        `}
      >
        {megaNavData.map((navItem) => {
          const isOpen = openNavItem === navItem.name;
          return (
            <Box as="li" key={navItem.name} position="relative">
              <MegaNavLink
                active={slug === navItem.href}
                href={navItem.href}
                name={navItem.name}
                isOpen={isOpen}
                onClick={() => toggleNavItem(navItem.name)}
              >
                {navItem.name}
              </MegaNavLink>
              {isOpen && (
                <MegaNavFlyout
                  item={navItem}
                  isVisible={isOpen}
                  onMouseEnter={() => {}}
                  onMouseLeave={() => {}}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const MegaNavLink: React.FC<
  PropsWithChildren<{
    href: string;
    active?: boolean;
    name: string;
    isOpen: boolean;
    onClick: () => void;
  }>
> = ({ children, name, active, isOpen, onClick }) => (
  <Box
    as="button"
    display="inline-flex"
    alignItems="center"
    lineHeight="100"
    fontWeight="medium"
    py={3}
    px={5}
    h={HEADER_HEIGHT}
    onClick={(e: React.MouseEvent) => {
      e.preventDefault();
      onClick();
    }}
    aria-label="View link"
    aria-current={active}
    aria-expanded={isOpen}
    analyticsName={`cosmos-mega-nav-v2-nav-link-${name}`}
    css={css`
      background: transparent;
      border: none;
      cursor: pointer;
      color: ${novo.colors.warmgrey950};
      text-decoration: none;
      gap: 8px;

      &:hover,
      &:focus {
        color: ${novo.colors.brandBlue600};
        outline: 0;
      }
      &:active {
        color: ${novo.colors.brandBlue600};
      }
    `}
  >
    {children}
    <Box
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      width="0.6em"
      height="0.6em"
      css={css`
        transition: transform 0.2s ease;
        transform: ${isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
      `}
    >
      <EiChevronDownIcon />
    </Box>
  </Box>
);
